import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, Spinner, Form, Button, Nav, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CountUp from "react-countup";
import { Bar, Doughnut } from "react-chartjs-2";
import { Input } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import paths from "routes/paths";
import * as pmService from "services/pmService";
import SubtleBadge from "components/common/SubtleBadge";
import { useAuth } from "contexts/AuthContext";
import apiClient from "utils/apiClient";
import { getTaskAssigneeInfo } from "utils/taskAssignee";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const STATUS_CONFIG = {
  OPEN: { bg: "primary", icon: "folder-open", label: "Open" },
  IN_PROGRESS: { bg: "warning", icon: "spinner", label: "In Progress" },
  COMPLETED: { bg: "success", icon: "check-circle", label: "Completed" },
  BLOCKED: { bg: "danger", icon: "ban", label: "Blocked" },
};

const PRIORITY_CONFIG = {
  0: { bg: "secondary", label: "None" },
  1: { bg: "info", label: "Low" },
  2: { bg: "warning", label: "Medium" },
  3: { bg: "danger", label: "High" },
  4: { bg: "dark", label: "Critical" },
};

const PROFILE_BASE_URL = "/profile-service/api/v1/user-profiles";
const UNKNOWN_ASSIGNEE_VALUE = "__unknown__";

const getTaskAssigneeId = (task = {}) => {
  const id = task?.assignee?.id || task?.assigneeId || task?.assignee_id || null;
  return id ? String(id) : null;
};

const normalizeUserProfile = (profile = {}, fallbackId = null) => {
  const firstName = profile?.firstName || "";
  const lastName = profile?.lastName || "";
  const fullName =
    String(profile?.fullName || "").trim() ||
    `${firstName} ${lastName}`.trim() ||
    profile?.username ||
    "";

  return {
    id: profile?.id || fallbackId || null,
    email:
      profile?.email ||
      profile?.contactDetails?.personalEmail ||
      profile?.contactDetails?.workEmail ||
      null,
    firstName,
    lastName,
    fullName,
    username: profile?.username || null,
    imageUrl: profile?.imageUrl || profile?.profilePhoto || null,
  };
};

const hydrateTasksWithLatestAssigneeProfiles = async (tasks = []) => {
  const assigneeIds = [
    ...new Set((tasks || []).map(getTaskAssigneeId).filter(Boolean)),
  ];

  if (assigneeIds.length === 0) return tasks;

  const byAssigneeId = new Map();
  const results = await Promise.allSettled(
    assigneeIds.map((id) =>
      apiClient.get(`${PROFILE_BASE_URL}/${id}?refresh=true`),
    ),
  );

  results.forEach((result, index) => {
    if (result.status !== "fulfilled") return;
    const assigneeId = assigneeIds[index];
    byAssigneeId.set(
      assigneeId,
      normalizeUserProfile(result.value?.data, assigneeId),
    );
  });

  return (tasks || []).map((task) => {
    const assigneeId = getTaskAssigneeId(task);
    if (!assigneeId) return task;

    const profile = byAssigneeId.get(assigneeId);
    if (!profile) return task;
    const fallbackFullName =
      task?.assignee?.fullName ||
      `${task?.assignee?.firstName || ""} ${task?.assignee?.lastName || ""}`.trim() ||
      null;

    return {
      ...task,
      assigneeId: profile.id || task.assigneeId || task.assignee_id || assigneeId,
      assignee: {
        ...(task.assignee || {}),
        id: profile.id || assigneeId,
        email: profile.email || task?.assignee?.email || null,
        firstName: profile.firstName || task?.assignee?.firstName || "",
        lastName: profile.lastName || task?.assignee?.lastName || "",
        fullName:
          profile.fullName ||
          fallbackFullName,
        username: profile.username || task?.assignee?.username || null,
        imageUrl:
          profile.imageUrl !== null && profile.imageUrl !== undefined
            ? profile.imageUrl
            : task?.assignee?.imageUrl || null,
      },
      assigneeName: profile.fullName || fallbackFullName,
    };
  });
};

const StatCard = ({ title, value, icon, bg, onClick, subtitle }) => (
  <Card
    className="mb-3 overflow-hidden custom-shadow"
    style={{ cursor: onClick ? "pointer" : "default" }}
    onClick={onClick}
  >
    <Card.Body className="position-relative">
      <div className="d-flex align-items-center">
        <div
          className={`icon-circle bg-soft-${bg} d-flex align-items-center justify-content-center rounded-circle me-3`}
          style={{ width: 48, height: 48, minWidth: 48 }}
        >
          <FontAwesomeIcon icon={icon} className={`text-${bg} fs-5`} />
        </div>
        <div>
          <h6 className="mb-0 text-700">{title}</h6>
          <h3 className="mb-0 fw-bold text-900">
            <CountUp end={value} duration={1.5} separator="," />
          </h3>
          {subtitle && <small className="text-500">{subtitle}</small>}
        </div>
      </div>
    </Card.Body>
  </Card>
);

const PMDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [projects, setProjects] = useState([]);
  const [iterations, setIterations] = useState([]);
  const [allTasks, setAllTasks] = useState([]);
  const [tags, setTags] = useState([]);
  const [myComments, setMyComments] = useState([]);
  const [myCommentsLoading, setMyCommentsLoading] = useState(false);
  const [userComments, setUserComments] = useState([]);
  const [userCommentsLoading, setUserCommentsLoading] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState({
    projectId: "",
    iterationId: "",
    tagIds: [],
    userId: "",
  });

  // Search state for filters
  const [searchQueries, setSearchQueries] = useState({
    project: "",
    iteration: "",
    tag: "",
    user: "",
  });

  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // Get user name for personalized filtering
  const userName = useMemo(() => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.username || user?.email || "";
  }, [user]);

  useEffect(() => {
    const loadDashboard = async () => {
      try {
        setLoading(true);

        // Fetch projects and tags in parallel
        const [projectsPage, tagsData] = await Promise.all([
          pmService.listProjects({ page: 0, size: 100 }),
          pmService.listTags(),
        ]);

        const projectsList = projectsPage.content || [];
        setProjects(projectsList);
        setTags(Array.isArray(tagsData) ? tagsData : tagsData.content || []);

        // Fetch tasks for all projects
        const taskPromises = projectsList.map((p) =>
          pmService
            .listTasks(p.id, { page: 0, size: 1000 })
            .then((res) => res.content || [])
            .catch(() => [])
        );
        const taskArrays = await Promise.all(taskPromises);
        const flattenedTasks = taskArrays.flat();
        const hydratedTasks = await hydrateTasksWithLatestAssigneeProfiles(flattenedTasks);
        setAllTasks(hydratedTasks);
      } catch (err) {
        console.error("Dashboard load error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  // Load users for filter
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setUsersLoading(true);
        const usersResponse = await apiClient.get(PROFILE_BASE_URL, {
          params: {
            page: 0,
            size: 10000,
            refresh: "true",
          },
        });
        const usersList = usersResponse?.data?.content || usersResponse?.data || [];
        // Transform to include full name
        const transformedUsers = usersList.map(u => ({
          ...u,
          fullName: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email || u.username || "",
        }));
        setAllUsers(transformedUsers);
      } catch (err) {
        console.error("Failed to load users:", err);
      } finally {
        setUsersLoading(false);
      }
    };
    loadUsers();
  }, []);

  // Fetch iterations when project is selected
  useEffect(() => {
    const fetchIterations = async () => {
      if (filters.projectId) {
        try {
          const response = await pmService.listIterations(filters.projectId, { page: 0, size: 100 });
          setIterations(response.content || []);
        } catch (err) {
          console.error("Failed to fetch iterations:", err);
          setIterations([]);
        }
      } else {
        setIterations([]);
      }
    };
    fetchIterations();
  }, [filters.projectId]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      // Reset iteration when project changes
      ...(field === "projectId" && { iterationId: "" }),
    }));

    if (field === "projectId") {
      setSearchQueries((prev) => ({
        ...prev,
        iteration: "",
      }));
    }
  };

  // Handle user filter change
  const handleUserInputChange = (value) => {
    handleSearchChange("user", value);
    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue) {
      handleFilterChange("userId", "");
      return;
    }

    if (normalizedValue === "unknown") {
      handleFilterChange("userId", UNKNOWN_ASSIGNEE_VALUE);
      return;
    }

    const matchedUser = searchableUsers.find(
      (user) => user.fullName.toLowerCase() === normalizedValue
    );
    handleFilterChange("userId", matchedUser?.id || "");
  };

  const handleSearchChange = (field, value) => {
    setSearchQueries((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const getProjectOptionLabel = (project) =>
    `${project.name}${project.code ? ` (${project.code})` : ""}`;

  const handleProjectInputChange = (value) => {
    handleSearchChange("project", value);
    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue) {
      handleFilterChange("projectId", "");
      return;
    }

    const matchedProject = projects.find(
      (project) => getProjectOptionLabel(project).toLowerCase() === normalizedValue
    );
    handleFilterChange("projectId", matchedProject?.id || "");
  };

  const handleIterationInputChange = (value) => {
    handleSearchChange("iteration", value);
    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue) {
      handleFilterChange("iterationId", "");
      return;
    }

    const matchedIteration = iterations.find(
      (iteration) => iteration.name.toLowerCase() === normalizedValue
    );
    handleFilterChange("iterationId", matchedIteration?.id || "");
  };

  const handleTagInputChange = (value) => {
    handleSearchChange("tag", value);
    const normalizedValue = value.trim().toLowerCase();

    if (!normalizedValue) {
      setFilters((prev) => ({ ...prev, tagIds: [] }));
      return;
    }

    const matchedTag = tags.find((tag) => tag.name.toLowerCase() === normalizedValue);
    setFilters((prev) => ({
      ...prev,
      tagIds: matchedTag ? [matchedTag.id] : [],
    }));
  };

  const clearFilters = () => {
    setFilters({
      projectId: "",
      iterationId: "",
      tagIds: [],
      userId: "",
    });
    setSearchQueries({
      project: "",
      iteration: "",
      tag: "",
      user: "",
    });
  };

  // Filtered options based on search - for dropdown display
  const searchableProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchQueries.project.toLowerCase()) ||
    (p.code && p.code.toLowerCase().includes(searchQueries.project.toLowerCase()))
  );

  const searchableIterations = iterations.filter(i =>
    i.name.toLowerCase().includes(searchQueries.iteration.toLowerCase())
  );

  const searchableTags = tags.filter(t =>
    t.name.toLowerCase().includes(searchQueries.tag.toLowerCase())
  );

  const userOptions = useMemo(
    () => [
      { id: UNKNOWN_ASSIGNEE_VALUE, fullName: "Unknown", email: "" },
      ...allUsers,
    ],
    [allUsers],
  );

  const searchableUsers = userOptions.filter(u =>
    u.fullName.toLowerCase().includes(searchQueries.user.toLowerCase()) ||
    (u.email && u.email.toLowerCase().includes(searchQueries.user.toLowerCase()))
  );

  const normalizedAllTasks = useMemo(
    () =>
      (allTasks || []).map((task) => {
        const assignee = getTaskAssigneeInfo(task);
        return {
          ...task,
          assigneeName: assignee.label,
          assigneeEmail: assignee.assigneeEmail,
        };
      }),
    [allTasks],
  );

  // Filter tasks based on selected filters
  const filteredTasks = useMemo(() => {
    return normalizedAllTasks.filter((task) => {
      // Filter by project
      if (filters.projectId && task.projectId !== filters.projectId) {
        return false;
      }
      // Filter by iteration
      if (filters.iterationId && task.iterationId !== filters.iterationId) {
        return false;
      }
      // Filter by tags
      if (filters.tagIds.length > 0) {
        const taskTagIds = task.tags?.map((t) => t.id) || [];
        if (!filters.tagIds.some((id) => taskTagIds.includes(id))) {
          return false;
        }
      }
      // Filter by user - tasks where user is assignee or creator
      if (filters.userId) {
        if (filters.userId === UNKNOWN_ASSIGNEE_VALUE) {
          const unknownAssignee =
            !task.assigneeName || task.assigneeName === "Unknown";
          if (!unknownAssignee) {
            return false;
          }
        } else {
          const selectedUser = allUsers.find(u => u.id === filters.userId);
          if (selectedUser) {
            const userFullName = selectedUser.fullName;
            const userMatch = 
              task.assigneeName?.toLowerCase() === userFullName.toLowerCase() ||
              task.createdByName?.toLowerCase() === userFullName.toLowerCase();
            if (!userMatch) {
              return false;
            }
          }
        }
      }
      return true;
    });
  }, [normalizedAllTasks, filters, allUsers]);

  // Filter projects based on selection
  const filteredProjects = useMemo(() => {
    let filtered = projects;
    
    // Filter by project ID
    if (filters.projectId) {
      filtered = filtered.filter((p) => p.id === filters.projectId);
    }
    
    // Filter by user (owner)
    if (filters.userId) {
      const selectedUser = allUsers.find(u => u.id === filters.userId);
      if (selectedUser) {
        const userFullName = selectedUser.fullName;
        filtered = filtered.filter((p) => 
          p.ownerName?.toLowerCase() === userFullName.toLowerCase()
        );
      }
    }
    
    return filtered;
  }, [projects, filters.projectId, filters.userId, allUsers]);

  // Tasks for "My Dashboard" - only tasks assigned to or created by current user
  const myWorkTasks = useMemo(() => {
    if (!userName) return [];
    return normalizedAllTasks.filter(
      (task) =>
        task.assigneeName === userName || task.createdByName === userName
    );
  }, [normalizedAllTasks, userName]);

  // Apply filters to my work tasks
  const filteredMyWorkTasks = useMemo(() => {
    return myWorkTasks.filter((task) => {
      if (filters.projectId && task.projectId !== filters.projectId) {
        return false;
      }
      if (filters.iterationId && task.iterationId !== filters.iterationId) {
        return false;
      }
      if (filters.tagIds.length > 0) {
        const taskTagIds = task.tags?.map((t) => t.id) || [];
        if (!filters.tagIds.some((id) => taskTagIds.includes(id))) {
          return false;
        }
      }
      // Filter by user
      if (filters.userId) {
        if (filters.userId === UNKNOWN_ASSIGNEE_VALUE) {
          const unknownAssignee =
            !task.assigneeName || task.assigneeName === "Unknown";
          if (!unknownAssignee) {
            return false;
          }
        } else {
          const selectedUser = allUsers.find(u => u.id === filters.userId);
          if (selectedUser) {
            const userFullName = selectedUser.fullName;
            const userMatch = 
              task.assigneeName?.toLowerCase() === userFullName.toLowerCase() ||
              task.createdByName?.toLowerCase() === userFullName.toLowerCase();
            if (!userMatch) {
              return false;
            }
          }
        }
      }
      return true;
    });
  }, [myWorkTasks, filters, allUsers]);

  // Calculate tasks by status
  const tasksByStatus = useMemo(() => {
    const tasks = activeTab === "overview" ? filteredTasks : filteredMyWorkTasks;
    const counts = { 
      OPEN: 0, 
      IN_PROGRESS: 0, 
      COMPLETED: 0, 
      BLOCKED: 0
    };
    tasks.forEach((t) => {
      if (counts[t.status] !== undefined) counts[t.status]++;
    });
    return counts;
  }, [filteredTasks, filteredMyWorkTasks, activeTab]);

  // Calculate tasks by priority
  const tasksByPriority = useMemo(() => {
    const tasks = activeTab === "overview" ? filteredTasks : filteredMyWorkTasks;
    const counts = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0 };
    tasks.forEach((t) => {
      if (counts[t.priority] !== undefined) counts[t.priority]++;
    });
    return counts;
  }, [filteredTasks, filteredMyWorkTasks, activeTab]);

  // Completed vs pending
  const completedTasks = tasksByStatus.COMPLETED;
  const pendingTasks = tasksByStatus.OPEN + tasksByStatus.IN_PROGRESS + tasksByStatus.BLOCKED;

  // Recent projects
  const recentProjects = useMemo(
    () =>
      [...(activeTab === "overview" ? filteredProjects : filteredProjects.filter(p => 
        myWorkTasks.some(t => t.projectId === p.id)
      ))]
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 5),
    [filteredProjects, myWorkTasks, activeTab]
  );

  // Recent tasks
  const recentTasks = useMemo(
    () =>
      [...(activeTab === "overview" ? filteredTasks : filteredMyWorkTasks)]
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
        .slice(0, 8),
    [filteredTasks, filteredMyWorkTasks, activeTab]
  );

  // Current data based on tab
  const currentTasks = activeTab === "overview" ? filteredTasks : filteredMyWorkTasks;
  const currentProjects = activeTab === "overview" ? filteredProjects : filteredProjects.filter(p => 
    myWorkTasks.some(t => t.projectId === p.id)
  );
  const myDashboardCount = useMemo(
    () =>
      filteredMyWorkTasks.filter(
        (task) => task.status === "OPEN" || task.status === "IN_PROGRESS"
      ).length,
    [filteredMyWorkTasks]
  );

  useEffect(() => {
    const loadMyComments = async () => {
      if (activeTab !== "mywork") {
        setMyComments([]);
        return;
      }

      const tasksToLoad = filteredMyWorkTasks
        .filter(
          (task) => task.status === "OPEN" || task.status === "IN_PROGRESS"
        )
        .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
        .slice(0, 20);

      if (tasksToLoad.length === 0) {
        setMyComments([]);
        return;
      }

      const identityCandidates = new Set(
        [
          userName,
          user?.username,
          user?.email,
          `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase())
      );

      try {
        setMyCommentsLoading(true);
        const commentsPerTask = await Promise.all(
          tasksToLoad.map(async (task) => {
            try {
              const response = await pmService.listComments(task.projectId, task.id);
              const comments = Array.isArray(response) ? response : response?.content || [];
              return comments.map((comment) => ({
                ...comment,
                taskId: task.id,
                taskTitle: task.title,
                projectId: task.projectId,
                iterationId: task.iterationId || "_",
              }));
            } catch (err) {
              return [];
            }
          })
        );

        const authoredByUser = commentsPerTask
          .flat()
          .filter((comment) => {
            const author =
              comment.createdByName ||
              comment.authorName ||
              `${comment.author?.firstName || ""} ${comment.author?.lastName || ""}`.trim();
            return identityCandidates.has(String(author || "").toLowerCase());
          })
          .sort((a, b) => new Date(b.createdAt || b.createdOn || 0) - new Date(a.createdAt || a.createdOn || 0))
          .slice(0, 10);

        setMyComments(authoredByUser);
      } finally {
        setMyCommentsLoading(false);
      }
    };

    loadMyComments();
  }, [
    activeTab,
    filteredMyWorkTasks,
    userName,
    user?.username,
    user?.email,
    user?.firstName,
    user?.lastName,
  ]);

  // Load comments when user filter is applied
  useEffect(() => {
    const loadUserComments = async () => {
      if (!filters.userId) {
        setUserComments([]);
        return;
      }

      if (filters.userId === UNKNOWN_ASSIGNEE_VALUE) {
        setUserComments([]);
        return;
      }

      const selectedUser = allUsers.find(u => u.id === filters.userId);
      if (!selectedUser) {
        setUserComments([]);
        return;
      }

      const userFullName = selectedUser.fullName;
      
      // Get tasks associated with the selected user
      const userTasks = normalizedAllTasks.filter(task => 
        task.assigneeName?.toLowerCase() === userFullName.toLowerCase() ||
        task.createdByName?.toLowerCase() === userFullName.toLowerCase()
      );

      const tasksToLoad = userTasks
        .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0))
        .slice(0, 30);

      if (tasksToLoad.length === 0) {
        setUserComments([]);
        return;
      }

      try {
        setUserCommentsLoading(true);
        const commentsPerTask = await Promise.all(
          tasksToLoad.map(async (task) => {
            try {
              const response = await pmService.listComments(task.projectId, task.id);
              const comments = Array.isArray(response) ? response : response?.content || [];
              return comments.map((comment) => ({
                ...comment,
                taskId: task.id,
                taskTitle: task.title,
                projectId: task.projectId,
                iterationId: task.iterationId || "_",
              }));
            } catch (err) {
              return [];
            }
          })
        );

        const authoredByUser = commentsPerTask
          .flat()
          .filter((comment) => {
            const author =
              comment.createdByName ||
              comment.authorName ||
              `${comment.author?.firstName || ""} ${comment.author?.lastName || ""}`.trim();
            return author?.toLowerCase() === userFullName.toLowerCase();
          })
          .sort((a, b) => new Date(b.createdAt || b.createdOn || 0) - new Date(a.createdAt || a.createdOn || 0))
          .slice(0, 10);

        setUserComments(authoredByUser);
      } finally {
        setUserCommentsLoading(false);
      }
    };

    loadUserComments();
  }, [filters.userId, allUsers, normalizedAllTasks]);

  // Chart data - Tasks by Status (Doughnut)
  const statusChartData = {
    labels: ["Open", "In Progress", "Completed", "Blocked"],
    datasets: [
      {
        data: [
          tasksByStatus.OPEN,
          tasksByStatus.IN_PROGRESS,
          tasksByStatus.COMPLETED,
          tasksByStatus.BLOCKED
        ],
        backgroundColor: [
          "#0d6efd", // primary - Open
          "#ffc107", // warning - In Progress
          "#198754", // success - Completed
          "#dc3545", // danger - Blocked
        ],
        borderWidth: 0,
      },
    ],
  };

  // Chart data - Tasks by Priority (Bar)
  const priorityChartData = {
    labels: ["None", "Low", "Medium", "High", "Critical"],
    datasets: [
      {
        label: "Tasks by Priority",
        data: [
          tasksByPriority[0],
          tasksByPriority[1],
          tasksByPriority[2],
          tasksByPriority[3],
          tasksByPriority[4],
        ],
        backgroundColor: [
          "#6c757d", // secondary
          "#0dcaf0", // info
          "#ffc107", // warning
          "#dc3545", // danger
          "#212529", // dark
        ],
        borderRadius: 4,
      },
    ],
  };

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
    },
  };

  const barChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const hasActiveFilters = filters.projectId || filters.iterationId || filters.tagIds.length > 0 || filters.userId;

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3 text-xs">Loading...</span>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <h5 className="mb-0 text-base">
          <FontAwesomeIcon
            icon="project-diagram"
            className="me-2 text-success"
          />
          Project Management Dashboard
        </h5>
      </div>

      {/* Tab Navigation */}
      {/* <Nav
        variant="tabs"
        className="mb-4"
        activeKey={activeTab}
        onSelect={setActiveTab}
      >
        <Nav.Item>
          <Nav.Link eventKey="overview" className="cursor-pointer">
            <FontAwesomeIcon icon="globe" className="me-2" />
            Overview
            <Badge bg="secondary" className="ms-2">
              {filteredTasks.length}
            </Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="mywork" className="cursor-pointer">
            <FontAwesomeIcon icon="user" className="me-2" />
            My Dashboard
            <Badge bg="success" className="ms-2">
              {myDashboardCount}
            </Badge>
          </Nav.Link>
        </Nav.Item>
      </Nav> */}

      <Nav
        className="premium-tabs mb-4"
        activeKey={activeTab}
        onSelect={setActiveTab}
      >
        <Nav.Item>
          <Nav.Link eventKey="overview">
            <FontAwesomeIcon icon="globe" className="me-2" />
            Overview
            <span className="tab-count">{filteredTasks.length}</span>
          </Nav.Link>
        </Nav.Item>

        <Nav.Item>
          <Nav.Link eventKey="mywork">
            <FontAwesomeIcon icon="user" className="me-2" />
            My Dashboard
            <span className="tab-count success">{myDashboardCount}</span>
          </Nav.Link>
        </Nav.Item>
      </Nav>

      {/* Filters Section */}
      <Card className="mb-4 custom-shadow">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-semibold">Project</Form.Label>
              <Input
                placeholder="Search project..."
                list="pm-dashboard-project-options"
                value={searchQueries.project}
                onChange={(e) => handleProjectInputChange(e.target.value)}
                allowClear
                size="small"
              />
              <datalist id="pm-dashboard-project-options">
                {searchableProjects.map((project) => (
                  <option
                    key={project.id}
                    value={getProjectOptionLabel(project)}
                  />
                ))}
              </datalist>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold">Iteration</Form.Label>
              <Input
                placeholder="Search iteration..."
                list="pm-dashboard-iteration-options"
                value={searchQueries.iteration}
                onChange={(e) => handleIterationInputChange(e.target.value)}
                disabled={!filters.projectId}
                allowClear
                size="small"
              />
              <datalist id="pm-dashboard-iteration-options">
                {searchableIterations.map((iteration) => (
                  <option key={iteration.id} value={iteration.name} />
                ))}
              </datalist>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold">Tag</Form.Label>
              <Input
                placeholder="Search tag..."
                list="pm-dashboard-tag-options"
                value={searchQueries.tag}
                onChange={(e) => handleTagInputChange(e.target.value)}
                allowClear
                size="small"
              />
              <datalist id="pm-dashboard-tag-options">
                {searchableTags.map((tag) => (
                  <option key={tag.id} value={tag.name} />
                ))}
              </datalist>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold">User</Form.Label>
              <Input
                placeholder="Search user..."
                list="pm-dashboard-user-options"
                value={searchQueries.user}
                onChange={(e) => handleUserInputChange(e.target.value)}
                allowClear
                size="small"
                suffix={usersLoading ? <Spinner size="sm" /> : null}
              />
              <datalist id="pm-dashboard-user-options">
                {searchableUsers.map((user) => (
                  <option key={user.id} value={user.fullName} />
                ))}
              </datalist>
            </Col>
            <Col md={2}>
              {hasActiveFilters && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={clearFilters}
                >
                  <FontAwesomeIcon icon="times" className="me-1" />
                  Clear
                </Button>
              )}
            </Col>
          </Row>
          {hasActiveFilters && (
            <small className="text-muted mt-2 d-block">
              Showing {currentTasks.length} tasks
              {activeTab === "mywork" && " (assigned to you)"}
            </small>
          )}
        </Card.Body>
      </Card>

      {/* Summary Cards */}
      <Row className="g-3 mb-4 justify-content-center">
        <Col xs={12} sm={6} md={4} lg>
          <StatCard
            title={activeTab === "overview" ? "Total Projects" : "My Projects"}
            value={currentProjects.length}
            icon="folder"
            bg="primary"
            onClick={() => navigate(paths.projects)}
          />
        </Col>

        <Col xs={12} sm={6} md={4} lg>
          <StatCard
            title="Total Tasks"
            value={currentTasks.length}
            icon="tasks"
            bg="info"
          />
        </Col>

        <Col xs={12} sm={6} md={4} lg>
          <StatCard
            title="Completed"
            value={completedTasks}
            icon="check-circle"
            bg="success"
          />
        </Col>

        <Col xs={12} sm={6} md={4} lg>
          <StatCard
            title="Pending"
            value={pendingTasks}
            icon="clock"
            bg="warning"
          />
        </Col>

        <Col xs={12} sm={6} md={4} lg>
          <StatCard
            title="Tags"
            value={tags.length}
            icon="tags"
            bg="secondary"
            onClick={() => navigate(paths.Tags)}
          />
        </Col>
      </Row>

      {activeTab === "mywork" && (
        <Row className="g-3 mb-4">
          <Col md={12}>
            <Card className="custom-shadow">
              <Card.Header>
                <h6 className="mb-0">
                  <FontAwesomeIcon
                    icon="comments"
                    className="me-2 text-primary"
                  />
                  My Recent Comments
                </h6>
              </Card.Header>
              <Card.Body className="p-0">
                {myCommentsLoading || userCommentsLoading ? (
                  <div className="text-center py-4">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <small className="text-muted d-block mt-2">
                      Loading comments...
                    </small>
                  </div>
                ) : (filters.userId ? userComments.length : myComments.length) === 0 ? (
                  <div className="text-center py-4 text-500">
                    {filters.userId ? `No comments from ${allUsers.find(u => u.id === filters.userId)?.fullName || 'this user'} yet` : 'No comments from you yet'}
                  </div>
                ) : (
                  <div className="list-group list-group-flush">
                    {(filters.userId ? userComments : myComments).map((comment) => (
                      <div
                        key={`${comment.taskId}-${comment.id}`}
                        className="list-group-item list-group-item-action px-3 py-2"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(
                            paths.projectTaskDetails(
                              comment.projectId,
                              comment.iterationId,
                              comment.taskId,
                            ),
                          )
                        }
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="me-2">
                            <div className="fw-semi-bold fs-10 text-900">
                              {comment.taskTitle}
                            </div>
                            <small className="text-700">
                              {comment.content || "No content"}
                            </small>
                          </div>
                          <small className="text-500 text-nowrap">
                            {new Date(
                              comment.createdAt ||
                                comment.createdOn ||
                                Date.now(),
                            ).toLocaleString()}
                          </small>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Charts Row */}
      <Row className="g-3 mb-4">
        <Col md={6}>
          <Card className="h-100 custom-shadow">
            <Card.Header>
              <h6 className="mb-0">
                <FontAwesomeIcon icon="chart-pie" className="me-2" />
                Tasks by Status
              </h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "250px" }}>
                <Doughnut data={statusChartData} options={chartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100 custom-shadow">
            <Card.Header>
              <h6 className="mb-0">
                <FontAwesomeIcon icon="chart-bar" className="me-2" />
                Tasks by Priority
              </h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "250px" }}>
                <Bar data={priorityChartData} options={barChartOptions} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Tasks by Status Cards */}
      <Row className="g-3 mb-4 justify-content-center">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => (
          <Col key={status} xs={12} sm={6} md={4} lg className="d-flex">
            <Card className="h-100 custom-shadow flex-fill">
              <Card.Body className="text-center py-3">
                <SubtleBadge bg={config.bg} className="mb-2 px-3 py-2">
                  <FontAwesomeIcon icon={config.icon} className="me-1" />
                  {config.label}
                </SubtleBadge>

                <h2 className="mb-0 fw-bold">
                  <CountUp end={tasksByStatus[status]} duration={1} />
                </h2>

                <small className="text-500">tasks</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-3">
        {/* Recent Projects */}
        <Col lg={5}>
          <Card className="h-100 custom-shadow">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <FontAwesomeIcon icon="folder" className="me-2 text-primary" />
                {activeTab === "overview" ? "Recent Projects" : "My Projects"}
              </h6>
              <a
                href="#!"
                className="fs-10 text-primary"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(paths.projects);
                }}
              >
                View All
              </a>
            </Card.Header>
            <Card.Body className="p-0">
              {recentProjects.length === 0 ? (
                <div className="text-center py-4 text-500">
                  {activeTab === "mywork"
                    ? "No projects assigned to you yet"
                    : "No projects yet"}
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {recentProjects.map((project) => (
                    <div
                      key={project.id}
                      className="list-group-item list-group-item-action px-3 py-2"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate(paths.projectDetails(project.id))}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <span className="fw-semi-bold text-900 fs-10">
                            {project.name}
                          </span>
                          <br />
                          <small className="text-500">
                            <code className="text-600">{project.code}</code>
                            {project.ownerName && (
                              <span className="ms-2">
                                <FontAwesomeIcon icon="user" className="me-1" />
                                {project.ownerName}
                              </span>
                            )}
                          </small>
                        </div>
                        <div className="text-end">
                          <small className="text-500">
                            {project.targetPoints != null && (
                              <span>
                                <FontAwesomeIcon
                                  icon="bullseye"
                                  className="me-1"
                                />
                                {project.targetPoints} pts
                              </span>
                            )}
                          </small>
                        </div>
                      </div>
                      {project.tags?.length > 0 && (
                        <div className="mt-1">
                          {project.tags.slice(0, 3).map((tag) => (
                            <SubtleBadge
                              key={tag.id}
                              bg="secondary"
                              className="me-1 fs-11"
                            >
                              {tag.name}
                            </SubtleBadge>
                          ))}
                          {project.tags.length > 3 && (
                            <small className="text-500">
                              +{project.tags.length - 3}
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Tasks */}
        <Col lg={7}>
          <Card className="h-100 custom-shadow">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <FontAwesomeIcon icon="tasks" className="me-2 text-info" />
                {activeTab === "overview" ? "Recent Tasks" : "My Tasks"}
              </h6>
            </Card.Header>
            <Card.Body className="p-0">
              {recentTasks.length === 0 ? (
                <div className="text-center py-4 text-500">
                  {activeTab === "mywork"
                    ? "No tasks assigned to you yet"
                    : "No tasks yet"}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0 fs-10">
                    <thead className="bg-light">
                      <tr>
                        <th>Title</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Points</th>
                        <th>Assignee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentTasks.map((task) => {
                        const statusCfg = STATUS_CONFIG[task.status] || {};
                        const priorityCfg =
                          PRIORITY_CONFIG[task.priority] || {};
                        const isOverdue =
                          task.dueDate &&
                          new Date(task.dueDate) < new Date() &&
                          task.status !== "COMPLETED";
                        return (
                          <tr
                            key={task.id}
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              navigate(
                                paths.projectTaskDetails(
                                  task.projectId,
                                  task.iterationId || "_",
                                  task.id,
                                ),
                              )
                            }
                          >
                            <td
                              className="text-truncate"
                              style={{ maxWidth: 200 }}
                            >
                              {task.title}
                              {isOverdue && (
                                <FontAwesomeIcon
                                  icon="exclamation-triangle"
                                  className="text-danger ms-1"
                                />
                              )}
                            </td>
                            <td>
                              <SubtleBadge bg={statusCfg.bg || "secondary"}>
                                {statusCfg.label || task.status}
                              </SubtleBadge>
                            </td>
                            <td>
                              <SubtleBadge bg={priorityCfg.bg || "secondary"}>
                                {priorityCfg.label || "—"}
                              </SubtleBadge>
                            </td>
                            <td>{task.points || "—"}</td>
                            <td
                              className="text-truncate"
                              style={{ maxWidth: 120 }}
                              title={task.assigneeEmail || "No assignee email available"}
                            >
                              {task.assigneeName || "Unknown"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

function getPriorityLabel(priority) {
  switch (priority) {
    case 0:
      return "None";
    case 1:
      return "Low";
    case 2:
      return "Medium";
    case 3:
      return "High";
    case 4:
      return "Critical";
    default:
      return "—";
  }
}

export default PMDashboard;
